import React, { useState, useEffect, useMemo } from 'react';
import * as yup from 'yup';
import { each } from 'lodash';
import PropTypes from 'prop-types';
import { useFormik } from 'formik';
import queryString from 'query-string';
import { useHistory, useLocation } from 'react-router-dom';
import { toastError, toastSuccess } from 'react-geek-toast';

import Button from 'partial/components/Button';
import FormPassword from 'partial/form/FormPassword';
import Form, { ErrorLabel } from 'partial/form/Form';

import { useForgotPassword } from '../hooks';

const validationSchema = yup.object().shape({
  password: yup
    .string()
    .required('Required')
    .min(8, 'At least 8 characters')
    .matches(/[A-Z]/, 'Must contain 1 uppercase letter')
    .matches(/[a-z]/, 'Must contain 1 lowercase letter')
    .matches(/[0-9]/, 'Must contain 1 numeric digit')
    .matches(
      /^(?=.*[!@#$%^&*])/,
      'Must contain at least 1 special character (!@#$%&*)'
    ),
  password_confirmation: yup
    .string()
    .required('Required')
    .oneOf([yup.ref('password')], 'Does not match'),
});

function NewPasswordForm({ onSuccess }) {
  const location = useLocation();
  const history = useHistory();
  const [validateOnChange, setValidateOnChange] = useState(false);

  const additionalPayload = useMemo(() => queryString.parse(location?.search), [
    location,
  ]);

  const { setNewPassword, isLoadingSetPassword: isLoading } = useForgotPassword(
    {
      onSetNewPassword: () => {
        toastSuccess('Password changed successfully.');
        onSuccess(true);
      },
    }
  );

  const { values, setFieldValue, handleSubmit, errors } = useFormik({
    initialValues: {
      password: '',
      password_confirmation: '',
    },
    validationSchema,
    onSubmit: (form, { setFieldError }) => {
      setNewPassword({ ...form, ...additionalPayload }, null, (err) => {
        each(err?.response?.errors, (v, k) => {
          setFieldError(k, v);
        });
      });
    },
    validateOnChange,
  });

  useEffect(() => {
    if (!queryString.parse(location?.search)?.token) {
      toastError('Session Expired.');
      history.push('/');
    }
  }, [location, history]);

  return (
    <Form
      onSubmit={handleSubmit}
      error={errors}
      noValidate
      className="w-full space-y-3"
    >
      <div>
        <FormPassword
          name="password"
          label="New Password"
          onSetFieldValue={setFieldValue}
          placeholder="Enter your new Password"
          value={values.password}
          error={errors.password}
          noAutoFill
          required
        />
        <ErrorLabel name="password" />
      </div>
      <div>
        <FormPassword
          name="password_confirmation"
          label="Confirm New Password"
          onSetFieldValue={setFieldValue}
          placeholder="Confirm your new Password"
          value={values.password_confirmation}
          error={errors.password_confirmation}
          noAutoFill
          required
        />
        <ErrorLabel name="password_confirmation" />
      </div>
      <Button
        primary
        type="submit"
        className="w-full"
        disabled={isLoading}
        onClick={() => setValidateOnChange(true)}
      >
        Reset Password
      </Button>
    </Form>
  );
}

NewPasswordForm.propTypes = {
  onSuccess: PropTypes.instanceOf(Function).isRequired,
};

export default NewPasswordForm;
